// Galleria — the cross-app doors (ASK-103 slice E, Client half).
//
// The console, meshsmith and the Studio have been three unlinked apps: a merchant who provisioned a
// shop in the console had no way to reach its Studio except by knowing a URL, and a merchant IN the
// Studio had no way back to the console's shop tools. Slice E gives that pair a door in each
// direction. This module owns the GRAMMAR of both doors — the URL shapes, the entry-mode gate, and
// the credential guard — as one import-free module so the runtime, the Studio chrome and the node
// suite all read the same rules (the `_authority.js` pattern).
//
// ─── S4 is the whole design: the door carries CONTEXT, not CREDENTIALS ──────────────────────────
// "Open in Studio" is a plain link to the active shop's runtime URL. No token, no claim, no role
// crosses the seam — the runtime does its own authority read (`_authority.js` → `GET /admin/me`,
// claim union otherwise) exactly as it does on a cold visit. `carriesCredential()` below is that
// rule made assertable: every door URL this module builds is checked against it, so a future edit
// that "helpfully" appends a token fails the suite instead of shipping a token in a shareable link.
//
// ─── The door NEVER elevates (S1) ──────────────────────────────────────────────────────────────
// `resolveEntryMode` can only ever hand back a mode the identity could already have reached by
// clicking the toggle. It reads the ALREADY-RESOLVED authority role — it does not read a claim, does
// not call anything, and has no path that returns an authoring mode for an identity the runtime
// would not have mounted the editor for. Deleting this module must not change what anyone is
// allowed to do; it only changes where they LAND.
//
// ─── Silence vs explanation (S3, as amended by Coord 2026-08-19) ────────────────────────────────
// An identity with NO claim path to authoring (a customer who was forwarded a `?mode=edit` link)
// gets Play with no explanation — a refusal naming a grant they will never hold is noise, not
// honesty. What DOES get said out loud is the door landing nowhere: `?shop=<id>` for a shop the boot
// has never heard of currently falls through to the hub in silence, which is the same
// "menu that lies" class the console fixed in slice A, and the user-facing face of the orphan-grant
// defect (D2) Shaun found in the switcher. `resolveDeepLinkShop` reports it so the runtime can say so.

// The Studio's mode vocabulary, unchanged — slice C ruled that a context/mode is named after what it
// IS, and these three words already exist (studio/editor-mode.js). The door introduces no fourth.
export const DOOR_MODES = ["play", "edit", "shell"];

// Console origin, PER PLANE (ASK-145 §14.2): since 2026-10-17 `admin.shopthegalaxy.co` is the PRODUCTION
// console and `admin.dev.shopthegalaxy.co` the dev one. Keyed by the plane's own word (`VITE_PLANE_WORD`,
// _features.js: alpha = dev, beta = prod), so a plane's console is decided by the same switch as the rest
// of its copy. The build REFUSES a `VITE_CONSOLE_URL` that is not its plane's console (`consoleEnvProblems`,
// run by runtime/vite.config.ts), and the no-config default is the DEV console — a boot that sets nothing
// may land on the wrong console, but never on production's.
export const CONSOLE_URL_BY_PLANE = Object.freeze({
	alpha: "https://admin.dev.shopthegalaxy.co",
	beta:  "https://admin.shopthegalaxy.co",
});
export const DEFAULT_CONSOLE_URL = CONSOLE_URL_BY_PLANE.alpha;

/** The console a plane's door opens, by its plane word; an unknown word reads as dev, never prod. */
export function consoleOriginForPlane(word) {
	return CONSOLE_URL_BY_PLANE[typeof word === "string" ? word.trim().toLowerCase() : ""] || DEFAULT_CONSOLE_URL;
}

/** Build check (vite.config.ts): the env's console must be its plane's console, exactly. [] = fine. */
export function consoleEnvProblems(env = {}) {
	const raw = typeof env.VITE_CONSOLE_URL === "string" ? env.VITE_CONSOLE_URL.trim().replace(/\/$/, "") : "";
	const want = consoleOriginForPlane(env.VITE_PLANE_WORD);
	if (!raw) return [`VITE_CONSOLE_URL is unset (this plane's console: ${want})`];
	if (raw !== want) return [`VITE_CONSOLE_URL=${raw} is not this plane's console (VITE_PLANE_WORD=${env.VITE_PLANE_WORD ?? "unset"} → ${want})`];
	return [];
}

// Query-parameter names — one place, because both ends of both doors have to agree on them and the
// far end (the console) is another lane's code.
export const SHOP_PARAM = "shop";           // runtime: which shop to boot into
export const MODE_PARAM = "mode";           // runtime: which Studio mode to land in
export const CONSOLE_SHOP_PARAM = "shopId";  // console: which shop context to select (slice D's own param)

// Anything that would make a door a credential-bearer. Matched case-insensitively against parameter
// NAMES; the guard is deliberately broader than what we emit today, since its job is to catch the
// edit nobody reviewed.
export const CREDENTIAL_PARAM_KEYS = [
	"token", "idtoken", "id_token", "access_token", "refresh_token", "bearer", "auth",
	"authorization", "jwt", "claim", "claims", "key", "apikey", "api_key", "secret",
	"password", "pw", "session", "sessionid", "sid", "signature", "sig",
];

/**
 * Does this URL carry anything credential-shaped? Checks query parameter names, the fragment (a
 * classic token-smuggling spot — `#access_token=...` never reaches a server log, which is exactly
 * why implicit-flow auth used it), and userinfo in the authority.
 * Pure; never throws — an unparseable string is reported as carrying nothing, since it isn't a door.
 */
export function carriesCredential(url) {
	if (!url || typeof url !== "string") return false;
	let parsed;
	try { parsed = new URL(url); } catch { return false; }
	if (parsed.username || parsed.password) return true;
	const named = (name) => CREDENTIAL_PARAM_KEYS.includes(String(name).toLowerCase());
	for (const key of parsed.searchParams.keys()) if (named(key)) return true;
	const hash = parsed.hash.replace(/^#/, "");
	if (hash) {
		// A fragment can be an opaque anchor OR a query-shaped payload; only the latter can smuggle.
		for (const pair of hash.split("&")) {
			const key = pair.split("=")[0];
			if (pair.includes("=") && named(key)) return true;
		}
	}
	return false;
}

/**
 * Read the door parameters off a location search string.
 * Returns `{ shop, mode }` — `shop` null when absent/blank, `mode` null when absent OR not one of
 * the three real modes (an unknown mode is not an error, it is simply not a door).
 */
export function parseDoorParams(search) {
	const out = { shop: null, mode: null };
	if (!search || typeof search !== "string") return out;
	let params;
	try { params = new URLSearchParams(search); } catch { return out; }
	const shop = (params.get(SHOP_PARAM) || "").trim();
	const mode = (params.get(MODE_PARAM) || "").trim().toLowerCase();
	if (shop) out.shop = shop;
	if (DOOR_MODES.includes(mode)) out.mode = mode;
	return out;
}

/**
 * Resolve `?shop=<id>` against the shops the boot actually carries.
 *
 * `{ shopId, unknown }` — `shopId` is the id to boot into (null → fall through to the hub as before),
 * and `unknown` is the id that was asked for but isn't in the boot. The two are never both set: an
 * id we can't honour is a door that landed nowhere, and the caller's job is to SAY so rather than
 * silently showing the hub and letting the operator conclude the console lied.
 */
export function resolveDeepLinkShop({ requestedShop, knownShopIds } = {}) {
	const requested = typeof requestedShop === "string" ? requestedShop.trim() : "";
	if (!requested) return { shopId: null, unknown: null };
	const known = Array.isArray(knownShopIds) ? knownShopIds : [];
	if (known.includes(requested)) return { shopId: requested, unknown: null };
	return { shopId: null, unknown: requested };
}

/**
 * Which mode should the runtime land in, given what the door asked for and what the identity turned
 * out to hold? Returns one of DOOR_MODES — never anything else, and never more than the identity
 * could reach on its own.
 *
 *   role                  → the ALREADY-RESOLVED authority role (`_authority.js`), not a claim.
 *   compositionBacked     → whether this shop can enter Shell at all (a fixed-shell shop can't).
 *
 * A non-authoring identity always lands in Play: the editor is not mounted for them at all, so any
 * other answer would be a mode the chrome can't honour.
 * A `shell` request on a fixed-shell shop degrades to `edit` rather than to `play` — the door was
 * asking for the authoring surface, and Edit is the authoring surface that exists here.
 */
export function resolveEntryMode({ requestedMode, role, compositionBacked = false } = {}) {
	if (!DOOR_MODES.includes(requestedMode)) return "play";
	if (role !== "merchant" && role !== "admin") return "play";
	if (requestedMode === "shell" && !compositionBacked) return "edit";
	return requestedMode;
}

/**
 * The door INTO the Studio — the exact URL the console's "Open in Studio" must produce.
 *
 * The console builds its own link (GCG's half of slice E), so this builder is not what runs in
 * production. It exists so the grammar has ONE executable definition on the receiving side: the
 * suite asserts the string, the ASK quotes it, and if the console ever drifts from it the drift is
 * against something testable rather than against prose.
 *
 * Returns null without a shop id — a door to "some shop" is not a door.
 */
export function studioUrlForShop({ runtimeBaseUrl, shopId, mode = "edit" } = {}) {
	const base = normaliseBase(runtimeBaseUrl);
	const shop = typeof shopId === "string" ? shopId.trim() : "";
	if (!base || !shop) return null;
	const url = new URL(base);
	url.searchParams.set(SHOP_PARAM, shop);
	// Play is the runtime's default; emitting `mode=play` would be a no-op parameter in a link people
	// paste around. Only an authoring mode is worth carrying.
	if (DOOR_MODES.includes(mode) && mode !== "play") url.searchParams.set(MODE_PARAM, mode);
	return assertTokenFree(url.toString());
}

/**
 * The door OUT of the Studio — back to this shop's console.
 *
 * Lands on the console root carrying `?shopId=`, which is slice D's own context parameter: the
 * console selects that shop as the active context and validates it against what the identity holds
 * (a context they no longer hold must not stick — slice D). We do not deep-link a sub-page: the
 * console owns its own IA, and a hardcoded route here would be this lane asserting a shape it
 * doesn't own.
 *
 * With no shop id (standing in the hub) the door still points at the console root rather than
 * disappearing — the way back should not blink out because the operator walked outside a shop.
 */
export function consoleUrlForShop({ consoleBaseUrl, shopId } = {}) {
	const base = normaliseBase(consoleBaseUrl || DEFAULT_CONSOLE_URL);
	if (!base) return null;
	const shop = typeof shopId === "string" ? shopId.trim() : "";
	const url = new URL(base);
	if (shop) url.searchParams.set(CONSOLE_SHOP_PARAM, shop);
	return assertTokenFree(url.toString());
}

/**
 * The shop a door out should point at, given where the player is standing.
 * The hub is not a shop — `kind === "hub"` yields null, and the door falls back to the console root.
 */
export function doorShopId({ projectId, kind } = {}) {
	if (!projectId || typeof projectId !== "string") return null;
	if (kind === "hub") return null;
	return projectId;
}

function normaliseBase(base) {
	if (!base || typeof base !== "string") return null;
	const trimmed = base.trim().replace(/\/+$/, "");
	if (!trimmed) return null;
	try { new URL(trimmed); } catch { return null; }
	return `${trimmed}/`;
}

// S4 enforced at the point of production, not only in the test: a door that somehow acquired a
// credential is not emitted at all. Loud, because silently dropping the parameter would hide the bug.
function assertTokenFree(url) {
	if (carriesCredential(url)) {
		console.error(`[doors] refused to emit a door carrying credential-shaped parameters (ASK-103 S4): ${url}`);
		return null;
	}
	return url;
}
